/**
 * A versioned block registry.
 *
 * Regta ships one (its legal catalog); a consumer building its own blocks
 * on this framework instantiates its own and can merge ours in. Nothing
 * here is Regta-specific — the registry is part of the exported language,
 * which is what lets another product author blocks that our assembler and
 * renderer accept unchanged.
 */
import { blockRef, parseBlockRef, toCatalogBlock } from './types.js';
export class BlockRegistry {
    #blocks = new Map();
    constructor(definitions = []) {
        for (const definition of definitions)
            this.register(definition);
    }
    register(definition) {
        const versions = this.#blocks.get(definition.blockKey) ?? new Map();
        if (versions.has(definition.version)) {
            throw new Error(`Duplicate block registration: ${blockRef(definition)}`);
        }
        versions.set(definition.version, definition);
        this.#blocks.set(definition.blockKey, versions);
        return this;
    }
    /** Merges another registry in. Duplicate `key@version` pairs throw. */
    merge(other) {
        for (const definition of other.list())
            this.register(definition);
        return this;
    }
    get(blockKey, version) {
        return this.#blocks.get(blockKey)?.get(version);
    }
    resolve(ref) {
        const parsed = parseBlockRef(ref);
        return parsed ? this.get(parsed.blockKey, parsed.version) : undefined;
    }
    /**
     * Newest *published* version of a block, by registration order. Drafts
     * are never resolved implicitly — an issued document must name its
     * version.
     */
    latestPublished(blockKey) {
        const versions = [...(this.#blocks.get(blockKey)?.values() ?? [])];
        return versions.filter((b) => b.status === 'published').at(-1);
    }
    list(filter = {}) {
        const statuses = filter.status
            ? new Set(Array.isArray(filter.status) ? filter.status : [filter.status])
            : null;
        const out = [];
        for (const [key, versions] of this.#blocks) {
            if (filter.blockKey && key !== filter.blockKey)
                continue;
            for (const definition of versions.values()) {
                if (filter.plane && definition.plane !== filter.plane)
                    continue;
                if (statuses && !statuses.has(definition.status))
                    continue;
                out.push(definition);
            }
        }
        return out;
    }
    /** The serializable catalog — what an API serves to a consumer. */
    catalog(filter = {}) {
        return this.list(filter).map((definition) => toCatalogBlock(definition));
    }
}
//# sourceMappingURL=registry.js.map